import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import './multiselect.style.scss';

/**
 * Liste déroulante à sélection multiple (cases à cocher dans le panneau).
 * Le contrôle affiche un résumé de la sélection ; le panneau se ferme au clic
 * extérieur ou sur Échap.
 */
const MultiSelect = ({
  name,
  options,
  values,
  onChange,
  placeholder,
  emptyLabel,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocumentClick = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selected = options.filter(option => values.includes(option.id));
  let summary = placeholder;
  if (selected.length === 1) {
    summary = selected[0].name;
  } else if (selected.length === 2) {
    summary = selected.map(option => option.name).join(', ');
  } else if (selected.length > 2) {
    summary = `${selected.length} models selected`;
  }

  const toggle = optionId => {
    onChange(
      values.includes(optionId)
        ? values.filter(value => value !== optionId)
        : [...values, optionId],
    );
  };

  return (
    <div
      className={`multiselect${disabled ? ' multiselect--disabled' : ''}${open ? ' multiselect--open' : ''}`}
      ref={containerRef}
    >
      <button
        type="button"
        name={name}
        className="multiselect__control"
        onClick={() => !disabled && setOpen(previous => !previous)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`multiselect__summary${selected.length ? '' : ' multiselect__summary--empty'}`}
        >
          {summary}
        </span>
        <span className="multiselect__arrow">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="multiselect__panel">
          <div className="multiselect__panel-header">
            <span>{`${selected.length} / ${options.length}`}</span>
            {selected.length > 0 && (
              <button type="button" className="multiselect__clear" onClick={() => onChange([])}>
                Clear
              </button>
            )}
          </div>
          <ul className="multiselect__list">
            {options.length === 0 ? (
              <li className="multiselect__empty">{emptyLabel}</li>
            ) : (
              options.map(option => (
                <li key={option.id} className="multiselect__list-item">
                  <label className="multiselect__item">
                    <input
                      type="checkbox"
                      className="multiselect__checkbox"
                      checked={values.includes(option.id)}
                      onChange={() => toggle(option.id)}
                    />
                    <span className="multiselect__label">{option.name}</span>
                  </label>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

MultiSelect.propTypes = {
  name: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  values: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  emptyLabel: PropTypes.string,
  disabled: PropTypes.bool,
};

MultiSelect.defaultProps = {
  name: '',
  values: [],
  placeholder: 'None',
  emptyLabel: 'No available option',
  disabled: false,
};

export default MultiSelect;
